import React, { useEffect, useMemo, useState } from 'react';
import { useOnboarding } from './ImportJobContext';
import { onboardingApi, IS_MOCK_API } from './onboarding.service';
import { ONBOARDING_LEAN } from './flags';
import { hairdresserService } from '../../_services/hairdresser.service';
import { decisionFor } from './decisions';
import { DuplicatePairCard, MockMergeView, UnmappedServiceCard, UnresolvedBookingCard, DecisionCard, StaffReviewCard } from './NeedsYouCards';
import MergeClientsView from '../clients/MergeClientsView';
import { IconTick, IconTickBig } from './icons';

/**
 * Step 2: "Needs you" (plan §4.3). One typed queue: duplicate pairs, staff
 * review, unresolved bookings, unmapped services, salon decisions. One card
 * at a time, card-out 320ms then the next. Always skippable.
 */

const CARD_OUT_MS = 320;

function isRelevant(item) {
  if (ONBOARDING_LEAN && (item.type === 'decision' || item.type === 'staff_review')) return false;
  if (item.type !== 'decision') return true;
  const def = decisionFor(item.key);
  return !!def && def.applies(item.context || {});
}

// Invite = the existing user/addStaff multipart call, same fields AddStaff.js sends.
async function inviteStaff(list) {
  if (IS_MOCK_API) return;
  const salonId = localStorage.getItem('salonId');
  for (const s of list) {
    const [first, ...rest] = (s.name || '').split(' ');
    const fd = new FormData();
    fd.append('name', s.name || '');
    fd.append('first_name', first || '');
    fd.append('last_name', rest.join(' '));
    fd.append('email', s.email);
    fd.append('phoneNumber', s.phoneNumber || '');
    fd.append('jobTitle', s.jobTitle || 'Stylist');
    fd.append('hairdresserBookingInterval', 15);
    fd.append('shortBio', '');
    fd.append('employmentTypeId', s.employmentTypeId || 1);
    fd.append('salonId', salonId);
    fd.append('countryId', '217');
    fd.append('currency', 'GBP');
    fd.append('currencySymbol', '£');
    fd.append('currencyCountry', 'GB');
    fd.append('salonCommissionRate', 0);
    fd.append('productCommissionRate', 0);
    fd.append('voucherCommissionRate', 0);
    fd.append('importTempId', s.tempId); // lets the BE link the invite to the job-created staff record
    // eslint-disable-next-line no-await-in-loop
    await hairdresserService.addStaff(fd);
  }
}

export default function NeedsYouQueue({ onDone, isMobile }) {
  const ob = useOnboarding();
  const { job, answer, mergeExact } = ob;
  const answers = job?.answers || {};
  const queue = useMemo(() => (job?.queue || []).filter(isRelevant), [job]);
  const pending = useMemo(() => queue.filter((q) => !answers[q.id]), [queue, answers]);
  const total = queue.length;
  const current = pending[0] || null;
  const index = total - pending.length; // 0-based position of current

  const [exiting, setExiting] = useState(false);
  const [merge, setMerge] = useState(null);       // item being merged
  const [mergingAll, setMergingAll] = useState(false);
  const [finished, setFinished] = useState(null); // { title, sub }
  const [busy, setBusy] = useState(false);

  const clients = job?.summary?.clients || 0;
  const exact = job?.summary?.exactDuplicates || 0;
  const exactDone = !!job?.exactMerged;

  const advance = async (itemId, ans) => {
    setExiting(true);
    setBusy(true);
    await new Promise((r) => setTimeout(r, CARD_OUT_MS));
    try { await answer(itemId, ans); } catch (e) { /* keep going; the queue is never blocking */ }
    setExiting(false);
    setBusy(false);
  };

  // Finish when nothing is left (after at least one render with a queue).
  useEffect(() => {
    if (!finished && total > 0 && !current && job?.status !== 'extracting') {
      const invited = Object.values(answers).some((a) => a && Array.isArray(a.invited) && a.invited.length);
      setFinished(invited
        ? { title: 'Done. Invites on their way.', sub: 'Your team will get an email to join. The rest is waiting in your checklist.' }
        : { title: 'Nice. All sorted.', sub: ONBOARDING_LEAN ? 'Now let us show you around.' : 'The rest is waiting in your checklist. No rush.' });
    }
  }, [current, total, finished, answers, job]);

  const skipAll = async () => {
    setBusy(true);
    for (const item of pending) {
      // eslint-disable-next-line no-await-in-loop
      try { await answer(item.id, { action: 'skip' }); } catch (e) { /* ignore */ }
    }
    setFinished({ title: 'No problem.', sub: pending.length === 1 ? 'It comes in as it is. Sort it any time from Clients.' : `All ${pending.length} come in as they are, duplicates included. Sort them any time from Clients.` });
    setBusy(false);
  };

  const doMergeAll = async () => {
    if (exactDone || mergingAll) return;
    setMergingAll(true);
    try { await mergeExact(); } catch (e) { /* toast-less: the row just stays */ }
    setMergingAll(false);
  };

  /* ---------- merge sub-screen ---------- */
  if (merge) {
    const done = async (result) => {
      setMerge(null);
      await advance(merge.id, { action: 'merged', mergeId: result?.mergeId || null });
    };
    if (IS_MOCK_API) {
      return (
        <MockMergeView
          pair={merge}
          onBack={() => setMerge(null)}
          onConfirmed={async () => done(await onboardingApi.mockConfirmMerge(job.jobId, { a: merge.a.clientId, b: merge.b.clientId }))}
        />
      );
    }
    const winner = (merge.a.visits || 0) >= (merge.b.visits || 0) ? merge.a : merge.b;
    const loser = winner === merge.a ? merge.b : merge.a;
    return (
      <div className="ob-screen">
        <div className="ob-screen-scroll">
          <MergeClientsView winnerUserId={winner.clientId} loserUserId={loser.clientId} onBack={() => setMerge(null)} onConfirmed={done} />
        </div>
      </div>
    );
  }

  /* ---------- finished ---------- */
  if (finished) {
    return (
      <div className="ob-screen">
        <div className="ob-eyebrow-row"><span className="ob-eyebrow">Needs you</span><span className="ob-counter-pill">{total} / {total}</span></div>
        <h2 className="ob-h1">All sorted for now.</h2>
        <div className="ob-done-block">
          <div className="ob-done-mark"><IconTickBig /></div>
          <div className="ob-done-title">{finished.title}</div>
          <div className="ob-done-sub">{finished.sub}</div>
        </div>
        <div className="ob-foot">
          <button type="button" className="ob-btn ob-btn--primary" onClick={onDone}>See my diary</button>
        </div>
      </div>
    );
  }

  /* ---------- empty queue straight away ---------- */
  if (!current) {
    return (
      <div className="ob-screen">
        <div className="ob-eyebrow-row"><span className="ob-eyebrow">Needs you</span></div>
        <h2 className="ob-h1">Nothing needs you.</h2>
        <div className="ob-done-block">
          <div className="ob-done-mark"><IconTickBig /></div>
          <div className="ob-done-sub">We could place everything ourselves. Your diary is ready.</div>
        </div>
        <div className="ob-foot">
          <button type="button" className="ob-btn ob-btn--primary" onClick={onDone}>See my diary</button>
        </div>
      </div>
    );
  }

  const isStaff = current.type === 'staff_review';
  const title = current.type === 'duplicate_pair' ? 'Same person, or two?'
    : isStaff ? 'Your team, before we invite them.'
    : current.type === 'unmapped_service' ? 'Where does this go?'
    : current.type === 'unresolved_booking' ? 'One booking needs a hand.'
    : 'Quick one.';

  let card = null;
  if (current.type === 'duplicate_pair') {
    card = <DuplicatePairCard item={current} exiting={exiting} onKeepBoth={() => advance(current.id, { action: 'keep_both' })} onSamePerson={() => setMerge(current)} />;
  } else if (current.type === 'unmapped_service') {
    card = <UnmappedServiceCard item={current} exiting={exiting} onAnswer={(a) => advance(current.id, a)} />;
  } else if (current.type === 'unresolved_booking') {
    card = <UnresolvedBookingCard item={current} exiting={exiting} onAnswer={(a) => advance(current.id, a)} />;
  } else if (current.type === 'decision') {
    card = <DecisionCard key={current.id} item={current} exiting={exiting} onAnswer={(a) => advance(current.id, a)} />;
  } else if (isStaff) {
    card = (
      <StaffReviewCard
        key={current.id}
        item={current}
        exiting={exiting}
        onInvite={async (invited, later) => { try { await inviteStaff(invited); } catch (e) { /* surfaced in checklist */ } await advance(current.id, { invited, later }); }}
        onLater={(later) => advance(current.id, { invited: [], later })}
      />
    );
  }

  return (
    <div className="ob-screen">
      <div className="ob-eyebrow-row">
        <span className="ob-eyebrow">Needs you</span>
        <span className="ob-counter-pill">{Math.min(index + 1, total)} / {total}</span>
      </div>
      <h2 className="ob-h1">{title}</h2>
      <div className="ob-screen-scroll">
        {card}
        {current.type === 'duplicate_pair' && exact > 0 && (
          <div className="ob-inline-action">
            <span>Merge all {exact.toLocaleString('en-GB')} exact matches at once</span>
            {exactDone ? <span className="ob-done"><IconTick size={12} /> Merged</span>
              : <button type="button" onClick={doMergeAll} disabled={mergingAll}>{mergingAll ? 'Merging…' : 'Merge all'}</button>}
          </div>
        )}
      </div>
      <div className="ob-foot">
        <button type="button" className="ob-btn ob-btn--ghost ob-btn--stack" onClick={skipAll} disabled={busy}>
          <span>Skip for now</span>
          <small>{pending.length === 1 ? 'This one comes in as it is. Sort it any time from Clients.' : `All ${pending.length} come in as they are, duplicates included. Sort them any time from Clients.`}</small>
        </button>
        {!isStaff && clients > 0 && (
          <div className="ob-footnote">{clients.toLocaleString('en-GB')} clients are ready to go. These {total} are the only ones we could not call ourselves.</div>
        )}
      </div>
    </div>
  );
}
